import { computed, Injectable, signal } from '@angular/core';

export type AnalyticsConsentValue = 'accepted' | 'rejected' | 'unset';

interface ConsentPreferences {
  analytics: AnalyticsConsentValue;
  updatedAt?: string;
}

const CONSENT_STORAGE_KEY = 'cookie_preferences_v1';

@Injectable({
  providedIn: 'root'
})
export class ConsentService {
  private preferences = signal<ConsentPreferences>(this.readStoredPreferences());

  readonly analyticsConsent = computed(() => this.preferences().analytics);
  readonly shouldShowBanner = computed(() => this.analyticsConsent() === 'unset');
  readonly isAnalyticsAccepted = computed(() => this.analyticsConsent() === 'accepted');

  acceptAnalytics(): void {
    this.updateAnalyticsConsent('accepted');
  }

  rejectAnalytics(): void {
    this.updateAnalyticsConsent('rejected');
  }

  saveAnalyticsConsent(enabled: boolean): void {
    this.updateAnalyticsConsent(enabled ? 'accepted' : 'rejected');
  }

  private updateAnalyticsConsent(value: Exclude<AnalyticsConsentValue, 'unset'>): void {
    const nextPreferences: ConsentPreferences = {
      analytics: value,
      updatedAt: new Date().toISOString()
    };

    this.preferences.set(nextPreferences);
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(nextPreferences));
  }

  private readStoredPreferences(): ConsentPreferences {
    const rawPreferences = localStorage.getItem(CONSENT_STORAGE_KEY);

    if (!rawPreferences) {
      return { analytics: 'unset' };
    }

    try {
      const parsed = JSON.parse(rawPreferences) as Partial<ConsentPreferences>;

      if (parsed.analytics === 'accepted' || parsed.analytics === 'rejected') {
        return {
          analytics: parsed.analytics,
          updatedAt: parsed.updatedAt
        };
      }
    } catch {
      localStorage.removeItem(CONSENT_STORAGE_KEY);

      return { analytics: 'unset' };
    }

    return { analytics: 'unset' };
  }
}
